import {
  FUNDING_OPTION_KEYS,
  isCharterFundingProgramSlug,
} from "@outschool/funding-programs-shared";
import { useTranslation } from "@outschool/localization";
import {
  joinMembershipPath,
  membershipStatusPage,
  onboardingQuizPath,
  searchPath,
} from "@outschool/routes";
import { gql, useQuery } from "@outschool/ui-apollo";
import { CurrentUser } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router";

import { BACK_TO_SCHOOL_HOMEPAGE_2026_FEATURE_FLAG } from "../../../shared/featureFlags";
import { BackToSchoolOfferBannerUserQueryQuery } from "../../../shared/generated/graphql";
import { usePaymentMethods } from "../../hooks/CurrentUser";
import { useAzEsaPathfinderEligibility } from "../../hooks/useAzEsaPathfinderEligibility";
import { useBannerDismissal } from "../../hooks/useBannerDismissal";
import { useCreditOffer } from "../../hooks/useCreditOffer";
import { usePublicMembershipOffer } from "../../hooks/usePublicMembershipOffer";
import { useFeatureFlag } from "../../lib/FeatureFlags";
import {
  BACK_TO_SCHOOL_OFFER_BANNER_NAME,
  BackToSchoolOfferBanner,
  BackToSchoolOfferBannerConfig,
  ESA_FALL_FUNDS_OFFER_BANNER_NAME,
  ESA_FALL_FUNDS_PROMO_CODE,
  NEW_TERM_PROMO_CODE,
} from "./BackToSchoolOfferBanner";
import BackupPaymentBanner, {
  shouldShowBackupPaymentBanner,
} from "./BackupPaymentBanner";
import CreditOfferBanner from "./CreditOfferBanner";
import FirstMonthFreeBanner from "./FirstMonthFreeBanner";
import PathfinderAzEsaBanner, {
  PATHFINDER_BANNER_NAME,
} from "./PathfinderAzEsaBanner";
import ReferralSignupBanner from "./ReferralSignupBanner";

const LAPSED_CUSTOMER_DAYS = 90;

const DEFAULT_EXCLUDED_PATHS = {
  exactPaths: [joinMembershipPath()],
  partialPaths: [
    onboardingQuizPath(),
    searchPath(),
    "/users/",
    membershipStatusPage(),
    "/teach/",
    "/account/teacher",
    "/class-comparison",
  ],
};

export function useShouldShowMembershipBannerByPath(): boolean {
  const location = useLocation();

  return React.useMemo(() => {
    if (DEFAULT_EXCLUDED_PATHS.exactPaths.includes(location.pathname)) {
      return false;
    } else if (
      DEFAULT_EXCLUDED_PATHS.partialPaths.some(path =>
        location.pathname.includes(path),
      )
    ) {
      return false;
    }

    return true;
  }, [location.pathname]);
}

type PaidEnrollment = {
  confirmedAt?: string | null;
  confirmed_at?: string | null;
};

const backToSchoolOfferBannerUserQuery = gql`
  query BackToSchoolOfferBannerUserQuery {
    currentUser {
      uid
      latestPaidEnrollment {
        uid
        confirmedAt
      }
    }
  }
`;

function getEnrollmentConfirmedAt(enrollment?: PaidEnrollment | null) {
  return enrollment?.confirmedAt ?? enrollment?.confirmed_at ?? null;
}

function isLapsedCustomer(enrollment?: PaidEnrollment | null) {
  const confirmedAt = getEnrollmentConfirmedAt(enrollment);
  if (!confirmedAt) {
    return false;
  }

  const confirmedAtMs = new Date(confirmedAt).getTime();
  if (Number.isNaN(confirmedAtMs)) {
    return false;
  }

  const lapsedCustomerMs = LAPSED_CUSTOMER_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - confirmedAtMs >= lapsedCustomerMs;
}

function hasCharterFundingOption(metadata: unknown) {
  if (
    typeof metadata !== "object" ||
    metadata === null ||
    !("selectedFundingOptionIds" in metadata)
  ) {
    return false;
  }

  const { selectedFundingOptionIds } = metadata;
  return (
    Array.isArray(selectedFundingOptionIds) &&
    selectedFundingOptionIds.includes(FUNDING_OPTION_KEYS.charter)
  );
}

function isCharterLearnerFundingProgram(
  fundingProgramSlug: string,
  metadata: unknown,
) {
  return (
    isCharterFundingProgramSlug(fundingProgramSlug) ||
    hasCharterFundingOption(metadata)
  );
}

function hasActiveNonCharterLearnerFundingProgram(currentUser: CurrentUser) {
  return Boolean(
    currentUser?.children?.some(child =>
      child.fundingPrograms?.some(
        fundingProgram =>
          !fundingProgram.deactivatedAt &&
          !isCharterLearnerFundingProgram(
            fundingProgram.fundingProgram.slug,
            fundingProgram.metadata,
          ),
      ),
    ),
  );
}

export function getLoggedInBackToSchoolOfferBannerConfig({
  currentUser,
  latestPaidEnrollment,
  membershipCouponCode,
  isTwentyDollarOfferEnabled,
  t,
}: {
  currentUser: CurrentUser;
  latestPaidEnrollment?: PaidEnrollment | null;
  membershipCouponCode: string;
  isTwentyDollarOfferEnabled: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}): BackToSchoolOfferBannerConfig | null {
  const paidEnrollment =
    latestPaidEnrollment ?? currentUser?.firstPaidEnrollment;
  const hasPurchased = Boolean(paidEnrollment);
  const isEsaFamily =
    currentUser?.hasUsedPublicFunding ||
    hasActiveNonCharterLearnerFundingProgram(currentUser);

  // ESA families identified at either the parent or learner level get
  // FALLFUNDS50 under a separate dismissal name. Charter learner programs do
  // not qualify; parent-level charter families and paid members are excluded.
  if (
    isEsaFamily &&
    !currentUser?.isCharterUser &&
    !currentUser?.hasSubscription
  ) {
    return {
      copy: t("ESA Families: get up to $50 off your next class"),
      code: ESA_FALL_FUNDS_PROMO_CODE,
      bannerName: ESA_FALL_FUNDS_OFFER_BANNER_NAME,
    };
  }

  // NEWTERM20 is only for users who have never made a purchase.
  if (!hasPurchased) {
    return {
      copy: t("$20 off your first class"),
      code: NEW_TERM_PROMO_CODE,
      bannerName: BACK_TO_SCHOOL_OFFER_BANNER_NAME,
    };
  }

  // No churned / lapsed-customer class offer post-BTS.
  if (isLapsedCustomer(paidEnrollment)) {
    return null;
  }

  if (currentUser?.hasEverSubscribed) {
    return null;
  }

  return {
    copy: isTwentyDollarOfferEnabled
      ? t("Get $20 off your first month")
      : t("Get your first month free"),
    code: membershipCouponCode,
    bannerName: BACK_TO_SCHOOL_OFFER_BANNER_NAME,
  };
}

/**
 * Render the appropriate membership banner based on the current user and location.
 * Only for logged-in users: see MembershipsBannerGate for logged-out users.
 */
export default function MembershipsBannerSwitcher({
  currentUser,
}: {
  currentUser: CurrentUser;
}) {
  // Top-level hooks.
  const { t } = useTranslation(
    "client\\components\\banners\\MembershipsBannerSwitcher",
  );
  const isBackToSchoolHomepageEnabled = useFeatureFlag(
    BACK_TO_SCHOOL_HOMEPAGE_2026_FEATURE_FLAG,
  );
  const { creditOffer, loading: creditOfferLoading } = useCreditOffer();
  const { isEligibleAzEsaNonClassWalletUser } = useAzEsaPathfinderEligibility();
  const { shouldShowBannersToday, isBannerDismissed } = useBannerDismissal();
  const shouldShowBannerByPath = useShouldShowMembershipBannerByPath();
  const { loading: paymentMethodsLoading, paymentMethods } =
    usePaymentMethods();
  const { couponCode: membershipCouponCode, isTwentyDollarOfferEnabled } =
    usePublicMembershipOffer();
  const {
    data: backToSchoolOfferBannerUserData,
    loading: latestPaidEnrollmentLoading,
  } = useQuery<BackToSchoolOfferBannerUserQueryQuery>(
    backToSchoolOfferBannerUserQuery,
    {
      skip: !isBackToSchoolHomepageEnabled,
    },
  );
  const location = useLocation();

  const firstMonthFreeUser = !currentUser?.hasEverSubscribed;

  // Special exception for the manual coupon experiment.
  // Please remove this block when the experiment is complete
  // Override all other banner logic on the membership subscribe page for first month free users.
  if (
    firstMonthFreeUser &&
    location.pathname.includes("/membership/subscribe/")
  ) {
    return <FirstMonthFreeBanner />;
  }

  // Wait for credit offer and payment methods to load before rendering the banner.
  // Respect banner dismissal and exclusion paths.
  if (
    creditOfferLoading ||
    paymentMethodsLoading ||
    latestPaidEnrollmentLoading ||
    !shouldShowBannersToday ||
    !shouldShowBannerByPath
  ) {
    return null;
  }

  const backToSchoolOfferBannerConfig = isBackToSchoolHomepageEnabled
    ? getLoggedInBackToSchoolOfferBannerConfig({
        currentUser,
        latestPaidEnrollment:
          backToSchoolOfferBannerUserData?.currentUser?.latestPaidEnrollment,
        membershipCouponCode,
        isTwentyDollarOfferEnabled,
        t,
      })
    : null;
  const shouldShowBackupBanner = shouldShowBackupPaymentBanner({
    currentUser,
    paymentMethods,
  });

  if (shouldShowBackupBanner) {
    return <BackupPaymentBanner />;
  } else if (
    isEligibleAzEsaNonClassWalletUser &&
    !isBannerDismissed(PATHFINDER_BANNER_NAME)
  ) {
    return <PathfinderAzEsaBanner />;
  } else if (creditOffer) {
    return <CreditOfferBanner />;
  } else if (
    backToSchoolOfferBannerConfig &&
    !isBannerDismissed(backToSchoolOfferBannerConfig.bannerName)
  ) {
    return <BackToSchoolOfferBanner config={backToSchoolOfferBannerConfig} />;
  } else {
    return <ReferralSignupBanner />;
  }
}
